/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */

import RemoteCase from '../case/remote-case';
import Utils from '../utils/utils';
import { getActiveContainerItemContext } from '../container/container-utils';
import { getDisplayCaseID } from '../case/common-helper';
import APP_CONSTANTS, { publicConstants } from '../constants';
import EnvironmentInfo from '../environment-info';
import type { RemoteCaseActionObject } from '../globals';

import { GlobalTOKEN, trimURL, getPlural, isDynamicPattern, isPortalParamExists } from './utils';
import { getPathNameOfUrl, sendMessageToConsole } from './browser-router';
import { isNextGenLandingPageRouting } from './helper';
import { resolveSemanticURL, routeProcessor, updateDocumentTitle } from './app-router';
import type {
  BuildRestAPIPayload,
  RoutingInfo,
  StaticRoutesList,
  RoutePayload,
  RouteObjectPayload,
  RouteData
} from './types';

/**
 * @file Implements parsing logic for Application Routing Table
 * @author WebWiz Team <WebWizDevelopmentTeam@pega.com>
 */

/**
 * RouteParser
 * This class consist of methods required for parsing App Routing Table
 */
class RouteParser {
  private routingInfo?: RoutingInfo;
  private appRoutes: any;
  private appRoutesMeta?: {
    [key: string]: {
      restApi: { reqType: string; endPoint: string; body: any; urlPattern: string; params: any };
      urlPattern: string;
    };
  };

  private appName?: string;
  private appAlias?: string;
  private orgName?: string;
  private reqContextName: string;
  private reqServletPath?: string;
  private defaultViewUrlPath: string;
  private staticRoutePage?: string;
  private defaultClassName: string;
  private readonly staticRoutesList?: StaticRoutesList = [];
  private readonly dynamicRoutesList?: string[];
  private appDelimeter?: string;
  private reqServletNameReal?: string;
  private bDefaultPortal?: boolean;
  private portalName: string;
  private ignoreFromEndPoint?: string[];

  /**
   * Creates an instance of RouteParser.
   * @memberof RouteParser
   */
  constructor() {
    this.staticRoutesList = [];
    this.dynamicRoutesList = [];
    this.reqContextName = '';
    this.defaultViewUrlPath = '';
    this.staticRoutePage = '';
    this.defaultClassName = '';
    this.portalName = '';
  }

  /**
   * This method initializes instance variables of Class
   * @function init
   * @param routingInfo - Application Routing Table Information
   * @param defaultViewUrlPath - url path of default view
   */
  init = (routingInfo: RoutingInfo, defaultViewUrlPath: string) => {
    if (routingInfo && routingInfo.routes && routingInfo.routesMeta) {
      this.routingInfo = routingInfo;
      this.appRoutes = routingInfo.routes;
      this.appRoutesMeta = routingInfo.routesMeta;
      this.reqContextName = routingInfo.reqContextName;
      this.reqServletPath = routingInfo.reqServletPath;
      this.appName = routingInfo.appName;
      this.appAlias = routingInfo.appAlias;
      this.orgName = routingInfo.orgName;
      this.defaultViewUrlPath = defaultViewUrlPath;
      this.defaultClassName = '';
      this._createStaticDynamicRoutesList(this.appRoutes);
      this.appDelimeter = routingInfo.appDelimeter;
      this.reqServletNameReal = routingInfo.pxReqServletNameReal;
      this.bDefaultPortal = routingInfo.isDefaultPortal;
      this.portalName = routingInfo.portalName;
      this.ignoreFromEndPoint = ['reactMode=dev', 'reactMode=prod'];
    } else {
      console.error(
        'Routing info is not appropriate. Missing routes and routesMeta objects in routingInfo',
        JSON.stringify(routingInfo)
      );
    }
  };

  /**
   * This method merges new routes to the existing routing table
   * @param routingInfo - new route entries with route and routeMeta
   */
  addRoutes = (routingInfo: RoutingInfo) => {
    if (routingInfo?.routes && routingInfo.routesMeta) {
      this.appRoutes = { ...this.appRoutes, ...routingInfo.routes };
      this.appRoutesMeta = { ...this.appRoutesMeta, ...routingInfo.routesMeta };

      if (this.routingInfo) {
        this.routingInfo.routes = { ...this.routingInfo.routes, ...routingInfo.routes };
        this.routingInfo.routesMeta = { ...this.routingInfo.routesMeta, ...routingInfo.routesMeta };
      }
      this._createStaticDynamicRoutesList(routingInfo.routes);
    }
  };

  setDefaultRoute = (defaultRoute: string) => {
    this.defaultViewUrlPath = defaultRoute;
  };

  setStaticRoutePage = (routePage: string) => {
    this.staticRoutePage = routePage;
  };

  getStaticRoutePage = () => {
    return this.staticRoutePage || '';
  };

  setDefaultClassName = (defaultClassName: string) => {
    this.defaultClassName = defaultClassName;
  };

  /**
   * this method separates static routes and dynamic routes of App Routing Table,
   * in two different arrays
   * @function _createStaticDynamicRoutesList
   * @param routesObj - routingInfo.routes
   */
  _createStaticDynamicRoutesList = (routesObj: { [key: string]: any }) => {
    const routesList = Object.keys(routesObj);
    routesList.forEach((routePattern) => {
      if (isDynamicPattern(routePattern)) {
        this.dynamicRoutesList?.push(routePattern);
      } else {
        this.staticRoutesList?.push({ [routePattern]: routesObj[routePattern] });
      }
    });
  };

  /**
   * this method returns key name after removing curly braces
   * @function _getDynamicKeyName
   * @param dynamicKey - dynamic key
   * @returns key name after removing curly braces
   */
  _getDynamicKeyName = (dynamicKey: string) => {
    const startIndex = dynamicKey.indexOf('{') + 1;
    const endIndex = dynamicKey.lastIndexOf('}');
    return dynamicKey.slice(startIndex, endIndex);
  };

  /**
   * this method returns true if constellation portal is default portal
   * @function isDefaultPortal
   * @returns returns isDefaultPortal
   */
  isDefaultPortal = () => {
    return `${this.bDefaultPortal}` !== 'false';
  };

  /**
   * this method returns constellation portal Name
   * @function getPortalName
   * returns portalName
   */
  getPortalName = () => {
    return this.portalName;
  };

  /**
   * this method returns application Path
   * @function getApplicationPath
   * @returns returns applicationPath
   */
  getApplicationPath = () => trimURL(`${this.getReqContextName()}/${this.getReqServletNameReal()}`);

  /**
   * this method returns application Name
   * @function getReqServletNameReal
   * @returns returns reqServletNameReal
   */
  getReqServletNameReal = () => {
    return `${this.reqServletNameReal}`;
  };

  /**
   * this method returns application Name
   * @function getAppDelimeter
   * @returns returns appDelimeter
   */
  getAppDelimeter = () => {
    return `${this.appDelimeter}`;
  };

  /**
   * this method returns application Name
   * @function getApplicationName
   * @returns returns applicationName
   */
  getApplicationName = () => {
    return `${this.appName}`;
  };

  getApplicationAlias = () => {
    return `${this.appAlias}`;
  };

  getServletPath = () => {
    return `${this.reqServletPath}`;
  };

  /**
   * this method returns application Name
   * @function getApplicationName
   * @returns returns applicationName
   */
  getOrganizationName = () => {
    return `${this.orgName}`;
  };

  /**
   * this method returns reqContextName(prweb)
   * @function getReqContextName
   * @returns returns reqContextName
   */
  getReqContextName = () => {
    return trimURL(this.reqContextName);
  };

  getRoutingInfo = () => {
    return this.routingInfo;
  };

  // /**
  //  * this method returns reqContextWithServletPath
  //  * @function getReqContextWithServletPath
  //  * @returns returns reqContextName with servlet if present
  //  */
  // getReqContextWithServletPath = () => {
  //     if(this.reqServletPath !== ""){
  //       return trimURL(this.reqContextName + "/" + this.reqServletPath);
  //     } else {
  //       return trimURL(this.reqContextName);
  //     }
  // };

  /**
   * this method returns processed semantic url
   * @function processUrl
   * @param semanticURL - semantic url
   * @param convertCase - convert case of semantic url?
   * @returns - processed semantic url
   */
  processUrl = (semanticURL: string, convertCase: boolean) => {
    if (convertCase) {
      semanticURL = semanticURL.toLowerCase();
    }
    if (semanticURL) {
      semanticURL = decodeURIComponent(semanticURL);
    }
    const patternforApplicationPath = new RegExp(this.getApplicationPath(), 'i');
    semanticURL = semanticURL.replace(patternforApplicationPath, '');

    // Reset the default class name if the URL is absolute and execution runtime info is available
    if (
      semanticURL.startsWith('/') &&
      isNextGenLandingPageRouting() &&
      EnvironmentInfo.getRenderingMode() !== 'PREVIEW'
    ) {
      this.setDefaultClassName('');
    }

    /* const patternforApplicationName = new RegExp(this.getApplicationName(), 'i');
    semanticURL = semanticURL.replace( patternforApplicationName,"");

     semanticURL = (this.appName).toLowerCase()+ "/" + trimURL(semanticURL);  */

    return trimURL(semanticURL);
  };

  stripIgnoredParams(semanticURLParam: string): string {
    this.ignoreFromEndPoint?.forEach((element: string) => {
      if (semanticURLParam.includes(element)) {
        semanticURLParam = semanticURLParam.substring(0, semanticURLParam.indexOf(element));
      }
    });
    return semanticURLParam;
  }

  getRouteDetails(matchedRoute: string, isRuntimeInfoAvailable: boolean): { routeKey: string; payload: RoutePayload } {
    if (!isRuntimeInfoAvailable) return this.appRoutes[matchedRoute];

    let routeKey = '';
    let payload: RoutePayload = {
      caseClassName: '',
      caseID: '',
      RemoteSystemID: undefined,
      page: ''
    };

    (Object.values(this.appRoutes) as RouteObjectPayload[]).forEach((route) => {
      if (route.payload?.page === matchedRoute) {
        routeKey = route.routeKey;
        payload = { ...route.payload };
      } else if (this.appRoutes[matchedRoute]) {
        routeKey = this.appRoutes[matchedRoute].routeKey;
        payload = { ...this.appRoutes[matchedRoute].payload };
      }
    });

    return { routeKey, payload };
  }

  transformRemoteCasePayload(payload: RoutePayload): RoutePayload {
    const { caseClassName, caseID } = payload;
    if (!RemoteCase.isRemoteCase(caseClassName)) return payload;

    const { RemoteClassGroup, RemoteSystemID } = RemoteCase.getRemoteCase(caseClassName);
    return {
      ...payload,
      caseID: caseID?.replace(caseClassName, RemoteClassGroup?.toUpperCase()),
      RemoteSystemID
    };
  }

  /**
   * this method returns endpoint and payload corresponding to semantic url
   * @function getEndPointInfo
   * @param semanticURLParam - semantic url
   * @returns - resolved route corresponding to semantic url
   */

  getEndPointInfo = (semanticURLParam: string) => {
    semanticURLParam = this.stripIgnoredParams(semanticURLParam);

    const isRuntimeInfoAvailable = isNextGenLandingPageRouting();
    let semanticURL = this.processUrl(semanticURLParam, true);
    let matchedRoute = this._getMatchedRouteInRoutingTable(
      semanticURL,
      isRuntimeInfoAvailable ? this.defaultClassName : undefined
    );

    if (!matchedRoute) {
      semanticURL = this.processUrl(this.defaultViewUrlPath, !isRuntimeInfoAvailable);
      matchedRoute =
        this._getMatchedRouteInRoutingTable(semanticURL, isRuntimeInfoAvailable ? this.defaultClassName : undefined) ||
        '';
    }

    const { routeKey, payload } = this.getRouteDetails(matchedRoute, isRuntimeInfoAvailable);
    if (!this.appRoutesMeta?.[routeKey]?.restApi) return null;

    const { reqType: requestType, endPoint: routeEndpoint, params } = this.appRoutesMeta[routeKey].restApi;
    const remoteCasePayload = this.transformRemoteCasePayload(payload);

    const routingInfo = {
      routeKey,
      requestType,
      routeEndpoint,
      payload: { ...params, ...remoteCasePayload }
    };

    return this._resolveRoute(routingInfo, this.processUrl(semanticURLParam, false), matchedRoute);
  };

  /**
   * Get semantic url for given routeKey (ex: showView)
   * @function getSemanticURL
   * @param routeKey - routeKey
   * @param payload - payload
   * @param queryParameters - queryParameters
   * @returns semantic url or empty string
   */
  getSemanticURL(routeKey: string, payload: any, queryParameters?: any) {
    return this.buildSemanticURL(routeKey, payload, queryParameters);
  }

  /**
   * Get resolved semantic url for given routeKey (example: openWorkByHandle)
   * @function getResolvedSemanticURL
   * @param routeKey - routeKey - This will be actionType : openWorkByHandle
   * @param payload - payload to match
   * @param params - dynamic param values
   * @returns resolved semantic url or empty string
   */
  getResolvedSemanticURL(routeKey: string, payload: any, params: { [key: string]: string } = {}) {
    const semanticURL = this.getSemanticURL(routeKey, payload, params);
    let resolvedSemanticURL = semanticURL;
    if (isDynamicPattern(semanticURL)) {
      const tokensList = semanticURL.match(GlobalTOKEN);
      tokensList?.forEach((token: string) => {
        const keyName = this._getDynamicKeyName(token);
        if (params[keyName]) {
          resolvedSemanticURL = resolvedSemanticURL.replace(token, params[keyName]);
        }
      });
    }
    const resolvedURL = resolveSemanticURL(resolvedSemanticURL);
    const url = new URL(resolvedURL);
    // We don't need to append portal in URL in case of default portals
    if (this.isDefaultPortal()) {
      return url.toString();
    }
    if (this.getPortalName() && !isPortalParamExists(url.searchParams)) {
      url.searchParams.append('portal', this.getPortalName());
    }
    return url.toString();
  }

  buildDocumentTitle(name?: string, caseId?: string, applicationName?: string): string {
    const appName = applicationName ?? '';
    return name && caseId ? `${name} (${caseId}) - ${appName}` : appName;
  }

  /**
   * Updates the semantic Url and document title.
   * @function updateSemanticURLAndDocumentTitle
   * @param data - data which contains key, className, actionName and isRemoteCase.
   */
  updateSemanticURLAndDocumentTitle(data: RemoteCaseActionObject) {
    const regex = /{.+?}/;
    const { key, actionName, caseClassName, isRemoteCase, name } = data;
    // If it is remote case scenario and className isn't present
    if (isRemoteCase && !caseClassName) {
      return;
    }
    const semanticURL = this.getSemanticURL(actionName, { caseClassName }).replace(
      regex,
      String(getDisplayCaseID(key))
    );
    const acName = `${publicConstants.APP.APP}${APP_CONSTANTS.FORWARD_SLASH_STRING}${publicConstants.PRIMARY}`;
    const translatedData = {
      semanticURL,
      directUrlUpdation: true,
      options: {
        acName,
        context: getActiveContainerItemContext(acName)
      }
    };

    const currentURL = getPathNameOfUrl();
    if (semanticURL && !currentURL.includes(semanticURL)) {
      routeProcessor(translatedData);
    }

    // Updating the document title
    const caseId = key.substring(key.lastIndexOf(' ') + 1);
    const applicationName = EnvironmentInfo.environmentInfoObject?.pxApplication?.pyLabel;
    const title = this.buildDocumentTitle(name, caseId, applicationName);
    updateDocumentTitle(title);
  }

  /**
   * Build semantic url for given routeKey (ex: showView)
   * @function buildSemanticURL
   * @param routeKey - routeKey
   * @param payload - payload
   * @param queryParameters - queryParameters object
   * @returns semantic url or empty string
   */
  buildSemanticURL(routeKey: string, payload: any, queryParameters?: any) {
    const { urlPattern } = this.appRoutesMeta?.[routeKey] || {};
    const urlPatterTokenCount = urlPattern?.split('/').length;
    const urlPatternTokens = urlPattern?.split('/');
    this.setDefaultClassName(payload?.pageClass || this.defaultClassName);
    const patternFilter = Object.keys(this.appRoutes)
      .filter((route) => {
        return urlPatterTokenCount === route.split('/').length;
      })
      .filter((route) => {
        const routeKeyTokens = route.split('?')[0].split('/');
        return urlPatternTokens?.every((token: string, index: number) => {
          return token.startsWith('{') || token === routeKeyTokens[index];
        });
      });
    if (patternFilter.length === 1) {
      return this._getQueryParamsResolvedURL(patternFilter[0], queryParameters);
    }
    if (Object.keys(payload).length === 0) {
      return '';
    }
    const payloadFilter = patternFilter.find((route) => {
      const routePayload = this.appRoutes[route].payload;
      const queryParams = { ...queryParameters };
      delete queryParams.dataViewName;
      delete queryParams.dataContext;
      const paramKeys = routePayload.dataViewKeys || routePayload.dataContextParameters || {};
      const matchedRoute = Object.keys(payload).every((prop) => payload[prop] === routePayload[prop]);
      if (matchedRoute) {
        if (!Utils.isEmpty(paramKeys) && !Utils.isEmpty(queryParams)) {
          return Object.keys(queryParams).every((prop) => Object.hasOwn(paramKeys, prop));
        }
        return matchedRoute;
      }
      return false;
    });
    if (payloadFilter) {
      return this._getQueryParamsResolvedURL(payloadFilter, queryParameters);
    }
    return patternFilter.includes('cases/{id}') && queryParameters?.id ? 'cases/{id}' : '';
  }

  /**
   * Resolves and return the dynamic parameters of queryParameter string
   * @param url - url
   * @param queryParameters - queryParameters object
   * @param ignoreCase - Flag to resolve the query parameters ignoring the case
   * @returns - Returns the url with resolved dynamic query parameters
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity
  _getQueryParamsResolvedURL(url: string, queryParameters?: any, ignoreCase = false) {
    const [path, queryParam] = url.includes('?') ? url.split('?') : [url];

    if (queryParam) {
      const queryParamsLowerCased: { [key: string]: any } = {};
      for (const [key, value] of Object.entries(queryParameters)) {
        queryParamsLowerCased[key.toLowerCase()] = value;
      }

      const searchParams = new URLSearchParams(queryParam);
      const resolvedQueryParams: { [key: string]: any } = {};
      for (const [key, value] of searchParams.entries()) {
        if (typeof value === 'string' && isDynamicPattern(value)) {
          const keyName = this._getDynamicKeyName(value);
          resolvedQueryParams[key] = ignoreCase
            ? queryParameters[keyName] || queryParamsLowerCased[keyName.toLowerCase()]
            : queryParameters[keyName];
        } else if (typeof value === 'string') {
          resolvedQueryParams[key] = value;
        }
      }

      const semanticURL = new URLSearchParams(resolvedQueryParams).toString();
      return `${path}?${semanticURL}`;
    }
    return path;
  }

  _getQueryParamsObject(queryParameters: any) {
    const queryParamsObj: { [key: string]: any } = {};
    const searchParams = new URLSearchParams(queryParameters);
    for (const [key, value] of searchParams.entries()) {
      queryParamsObj[key] = value;
    }
    return queryParamsObj;
  }

  /**
   * Get restApi for given routeKey (ex: showView)
   * @function getRestEndPoint
   * @param routeKey - routeKey
   * @param payload - payload
   * @returns restApi and reqType. Throws an error for invalid routeKey
   */
  getRestEndPoint(routeKey: string, payload?: BuildRestAPIPayload) {
    try {
      return this._buildRestApi(routeKey, payload);
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  /**
   * Build restApi for given routeKey (ex: showView)
   * @function _buildRestApi
   * @param routeKey - routeKey
   * @param payload - payload
   * @returns restApi and reqType. Throws an error for invalid routeKey
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity
  _buildRestApi(routeKey: string, payload?: BuildRestAPIPayload) {
    if (routeKey && this.appRoutesMeta?.[routeKey]) {
      const { reqType, body } = this.appRoutesMeta[routeKey].restApi;
      let { endPoint } = this.appRoutesMeta[routeKey].restApi;
      if (payload?.params && payload.params.pageName) {
        endPoint = `${endPoint}&pageName=${payload.params.pageName}`;
      }
      const dynamicTokens = endPoint.match(GlobalTOKEN);
      if (dynamicTokens) {
        for (let index = 0; index < dynamicTokens.length; index += 1) {
          const key = dynamicTokens[index].replace('{', '').replace('}', '');
          const pattern = new RegExp(dynamicTokens[index], 'g');
          endPoint = endPoint.replace(pattern, payload?.[key]);
        }
      }

      if (body) {
        let requestBody = {};
        const keys = Object.keys(body);
        keys.forEach((key) => {
          if (this._isSpecialInstruction(key)) {
            requestBody = { ...requestBody, ...this._processInstruction(key, body[key], payload) };
          }
        });

        return {
          restApi: endPoint,
          reqType,
          body: requestBody
        };
      }

      return {
        restApi: endPoint,
        reqType
      };
    }

    throw new Error(`Cannot find route metadata for the routeKey: ${routeKey}.`);
  }

  _isSpecialInstruction(key: string) {
    return key && key.startsWith('@');
  }

  _processInstruction(instruction: string, keys: [string], payload: any) {
    const processedObj: { [key: string]: string } = {};
    if (instruction === '@COPY') {
      keys.forEach((key) => {
        processedObj[key] = payload[key];
      });
    }
    return processedObj;
  }

  /**
   * Detects if a restApi exists with the given routeKey
   * @function doesRestApiExist
   * @param routeKey - routeKey
   * @returns Flag indicating whether the Api exists
   */
  doesRestApiExist(routeKey: string) {
    return !!(this.appRoutesMeta && this.appRoutesMeta[routeKey]);
  }

  /**
   * Resolve Dynamic Fragments from Endpoint and Payload
   * @function _resolveRoute
   * @param routingInfo - routing table entry
   * @param semanticURL - semantic url
   * @param matchedRoute - matched route pattern
   * @returns Resolved route
   */
  _resolveRoute = (routingInfo: any, semanticURL: string, matchedRoute: string) => {
    const urlPath: string[] = [];
    const [semanticURLPath, semanticURLQueryParams] = semanticURL.includes('?')
      ? semanticURL.split('?')
      : [semanticURL];
    const [matchedURLPath, matchedURLQueryParams] = matchedRoute.includes('?')
      ? matchedRoute.split('?')
      : [matchedRoute];
    const semanticURLPathTokens = semanticURLPath.split('/');
    const matchedRouteTokens = matchedURLPath.split('/');
    const tokensMap: { [key: string]: any } = {};

    for (let index = 0; index < matchedRouteTokens.length; index += 1) {
      urlPath[index] = matchedRouteTokens[index];
      if (isDynamicPattern(matchedRouteTokens[index])) {
        const keyName = this._getDynamicKeyName(matchedRouteTokens[index]);
        tokensMap[keyName] = semanticURLPathTokens[index];
        urlPath[index] = semanticURLPathTokens[index];
      }
    }

    const queryParamsObj = this._getQueryParamsObject(matchedURLQueryParams);
    const semanticURLQueryParamsObj = this._getQueryParamsObject(semanticURLQueryParams);
    const semanticURLQueryParamsLowerCased: { [key: string]: any } = {};

    for (const [key, value] of Object.entries(semanticURLQueryParamsObj)) {
      semanticURLQueryParamsLowerCased[key.toLowerCase()] = value;
    }

    for (const key of Object.keys(queryParamsObj)) {
      if (isDynamicPattern(queryParamsObj[key])) {
        // If semantic url has keys in lower case then they will not match key in route metadata. Checking for lowercase also.
        tokensMap[key] = semanticURLQueryParamsObj[key] || semanticURLQueryParamsLowerCased[key.toLowerCase()];
      } else {
        tokensMap[key] = queryParamsObj[key];
      }
    }

    /*
      If semantic url entered in the browser is
      data/employee-details?pyGUid=0759409f-4146-439c-aa25-57d4f495fee5
      instead of
      data/employee-details?pyGUID=0759409f-4146-439c-aa25-57d4f495fee5

      Get the proper semantic url to update in the browser using the matched route from the routing info
     */
    let queryParamsResolvedURL;
    if (semanticURLQueryParams) {
      queryParamsResolvedURL = this._getQueryParamsResolvedURL(matchedRoute, semanticURLQueryParamsObj, true);
    }

    const routingInfoWithResolvedPayload = this._resolvePayload(routingInfo, tokensMap, matchedRoute);
    return {
      ...this._resolveRouteEndPoint(routingInfoWithResolvedPayload, matchedRoute),
      urlPath: queryParamsResolvedURL || urlPath.join('/')
    };
  };

  /**
   * Process the RouteEndPoint inside JSON and remove dynamic params
   * @function _resolveRouteEndPoint
   * @param routingInfo - routing table entry
   * @param matchedRoute - matched route pattern
   * @returns Resolved route
   */
  _resolveRouteEndPoint = (routingInfo: any, matchedRoute: string) => {
    let { routeEndpoint } = routingInfo;
    // If static endpoint
    if (!isDynamicPattern(routeEndpoint)) {
      return routingInfo;
    }

    const errorObj: { [key: string]: any } = {};
    errorObj[matchedRoute] = routingInfo;

    // If endpoint has dynamic tokens
    if (routingInfo.payload !== undefined) {
      const tokensList = routeEndpoint.match(GlobalTOKEN);
      for (let index = 0; index < tokensList.length; index += 1) {
        const token = tokensList[index];
        const keyName = this._getDynamicKeyName(token);
        if (routingInfo.payload[keyName] !== undefined) {
          const resolvedValue = routingInfo.payload[keyName];
          routeEndpoint = routeEndpoint.replace(token, resolvedValue);
        } else {
          this._sendWarningAndErrorMessages(
            `Unable to resolve dynamic token {${keyName}} in routeEndpoint- ${keyName} is missing in payload`,
            errorObj
          );
        }
      }
    } else {
      this._sendWarningAndErrorMessages(
        'Unable to resolve dynamic token(s) in routeEndpoint- payload is missing in routing table for-',
        errorObj
      );
    }

    return { ...routingInfo, routeEndpoint };
  };

  /**
   * Process the replacePayload inside JSON and remove dynamic params
   * @function _resolvePayload
   * @param routingInfo - routing table entry
   * @param tokensMap - key-value pair: key(Dynamic Param in Matched Route), value(corresponding semantic url token)
   * @param matchedRoute - matched route pattern
   * @returns Resolved routing table entry
   */
  _resolvePayload = (routingInfo: any, tokensMap: any, matchedRoute: string) => {
    if (typeof routingInfo.payload === 'undefined') {
      return routingInfo;
    }

    const errorObj: { [key: string]: any } = {};
    errorObj[matchedRoute] = routingInfo;
    const routingInfoPayload = {
      ...routingInfo.payload,
      ...this._resolvePayloadRecursive(routingInfo.payload, tokensMap)
    };
    return { ...routingInfo, payload: routingInfoPayload };
  };

  _resolvePayloadRecursive(payload: any, tokensMap: any) {
    const resolvedPayload: { [key: string]: any } = {};
    for (const key of Object.keys(payload)) {
      let value = payload[key];
      if (typeof value === 'string' && isDynamicPattern(value)) {
        const tokensList: any = value.match(GlobalTOKEN);
        for (let index = 0; index < tokensList.length; index += 1) {
          const token = tokensList[index];
          const keyName = this._getDynamicKeyName(token);
          if (tokensMap[keyName] !== undefined) {
            const resolvedValue = tokensMap[keyName];
            value = value.replace(token, resolvedValue);
          } else {
            this._sendWarningAndErrorMessages(
              `Unable to resolve dynamic token {${keyName}} in payload- {${keyName}} is missing in semantic url construct`
            );
          }
        }
        resolvedPayload[key] = value;
      } else if (typeof value === 'object') {
        resolvedPayload[key] = this._resolvePayloadRecursive(value, tokensMap);
      }
    }
    return resolvedPayload;
  }

  /**
   * this method returns route in App Routing Table corresponding to semantic Url
   * @function _getMatchedRouteInRoutingTable
   * @param semanticURL - semantic url
   * @returns matched route or undefined
   */
  _getMatchedRouteInRoutingTable = (semanticURL: string, className?: string) => {
    // Remove query parameters from the semanticURL
    const [pathName] = semanticURL.includes('?') ? semanticURL.split('?') : [semanticURL];

    let matchedRoute = this._findMatchedRouteInStaticList(pathName, className);
    if (matchedRoute !== undefined) {
      return matchedRoute;
    }
    matchedRoute = this._findMatchedRouteInDynamicList(semanticURL);
    if (matchedRoute !== undefined) {
      return matchedRoute;
    }

    return undefined;
  };

  /**
   * this method returns static route if it matches semantic Url,otherwise returns undefined
   * @function _findMatchedRouteInStaticList
   * @param semanticURL - semantic url
   * @returns static route or undefined
   */
  _findMatchedRouteInStaticList = (semanticURL: string, className?: string): string | undefined => {
    if (!this.staticRoutesList || !semanticURL) return undefined;

    const normalizedURL = semanticURL.toLowerCase();
    const normalizedClass = className?.toLowerCase();

    if (isNextGenLandingPageRouting()) {
      const matchedRoute = this._findMatchingRouteByClass(normalizedURL, normalizedClass);
      if (!matchedRoute) return undefined;

      const routeData = Object.values(matchedRoute)[0] as RouteData;
      const routePage = routeData?.payload?.page || '';
      this.setStaticRoutePage(routePage);

      return Object.keys(matchedRoute)[0];
    }

    return this._findInStaticList(normalizedURL);
  };

  _findMatchingRouteByClass = (normalizedURL: string, normalizedClass?: string): any | undefined => {
    if (!this.staticRoutesList) return undefined;

    return this.staticRoutesList.find((routeObj) => {
      return Object.entries(routeObj).some(([routeKey, routeData]) => {
        if (!routeData) return false;

        const routePage = routeData.payload?.page?.toLowerCase();
        const routeClassName = routeData.payload?.pageClass?.toLowerCase();
        const urlMatch = routePage === normalizedURL || routeKey.toLowerCase() === normalizedURL;

        return normalizedClass ? urlMatch && routeClassName === normalizedClass : urlMatch;
      });
    });
  };

  _findInStaticList = (normalizedURL: string): string | undefined => {
    if (!this.staticRoutesList) return undefined;

    const matched = this.staticRoutesList.find((routeObj) => Object.keys(routeObj)[0].toLowerCase() === normalizedURL);

    return matched ? Object.keys(matched)[0] : undefined;
  };

  /**
   * this method returns dynamic route corresponding to semantic Url, otherwise returns undefined
   * @function _findMatchedRouteInDynamicList
   * @param semanticURL - semantic url
   * @returns dynamic route or undefined
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity
  _findMatchedRouteInDynamicList = (semanticURL: string) => {
    const [pathName, queryParams] = semanticURL.includes('?') ? semanticURL.split('?') : [semanticURL];
    const semanticURLPathTokens = pathName.split('/');
    // NOTE: find method is NOT supported in IE11
    return this.dynamicRoutesList?.find((dynamicRoute) => {
      const [dynamicRoutePathName, dynamicRouteQueryParams] = dynamicRoute.includes('?')
        ? dynamicRoute.split('?')
        : [dynamicRoute];
      const dynamicRoutePathTokens = dynamicRoutePathName.split('/');
      if (dynamicRoutePathTokens.length !== semanticURLPathTokens.length) {
        return false;
      }
      let index = 0;

      for (index = 0; index < dynamicRoutePathTokens.length; index += 1) {
        const token = dynamicRoutePathTokens[index].toLowerCase();
        if (
          !isDynamicPattern(token) &&
          token !== semanticURLPathTokens[index] &&
          getPlural(token) !== semanticURLPathTokens[index]
        ) {
          return false;
        }
      }

      // Matching the query parameters of the current url with the routes metadata ignoring the case of the parameters
      // Eg:
      // Search pattern - data/employee-details?pyGUID=0759409f-4146-439c-aa25-57d4f495fee5
      //                   or
      //                   data/employee-details?pyGuid=0759409f-4146-439c-aa25-57d4f495fee5

      // Route pattern  - data/employee-details?pyGUID={pyGUID}
      if (dynamicRouteQueryParams) {
        const searchParams = new URLSearchParams(queryParams.toLowerCase());
        const dynamicSearchParams = new URLSearchParams(dynamicRouteQueryParams.toLowerCase());
        const searchParamsKeys = new Set<string>();
        const dynamicSearchParamsKeys = new Set<string>();
        const dynamicValuePattern = /\{[a-zA-Z]+\}/;

        for (const key of searchParams.keys()) {
          searchParamsKeys.add(key);
        }

        for (const key of dynamicSearchParams.keys()) {
          dynamicSearchParamsKeys.add(key);
        }

        if (dynamicSearchParamsKeys.size !== searchParamsKeys.size) {
          return false;
        }

        for (const key of searchParamsKeys) {
          if (!dynamicSearchParamsKeys.has(key)) {
            return false;
          }
          const dynamicvalue = dynamicSearchParams.get(key) as string;
          if (searchParams.get(key) !== dynamicvalue && !dynamicValuePattern.test(dynamicvalue)) {
            return false;
          }
        }
      }

      return true;
    });
  };

  /**
   * this method send warning and error messages to console
   * @function _sendWarningAndErrorMessages
   * @param message - message
   * @param additionalInfo - additional information to be logged in console
   */
  _sendWarningAndErrorMessages = (message: any, additionalInfo?: any) => {
    sendMessageToConsole(message, additionalInfo, 'warning');
    sendMessageToConsole('Error in Generated Routing Table', null, 'error');
    sendMessageToConsole('Back | Forward for this page may NOT work as intended', null, 'warning');
    sendMessageToConsole('Bookmark | Refresh for this page may NOT work as intended', null, 'warning');
  };
}

const appRouteParser = new RouteParser();
export default appRouteParser;
